/**
 * Settings → Getting started. Quick 261013-srg PR 1: the 7 setup-wizard step cards and the
 * step editors, moved unchanged from the old Setup & Configuration page (Phase 24 / Task 24.8,
 * DEC-099). Edit opens that step's form standalone (same components as the wizard). PR 4
 * replaces the cards with a checklist that links out to where each job is done.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { WIZARD_STEPS, useWizardStatus, useUpdateWizardStatus } from '../setup-wizard/hooks';
import {
  CompanyInfoStep,
  OperatorsStep,
  DisposalStep,
  YardsStep,
  FleetStep,
  DriversStep,
  JobTypesStep,
  type StepProps,
} from '../setup-wizard/steps';

function renderStep(step: number, props: StepProps): React.JSX.Element {
  switch (step) {
    case 1:
      return <CompanyInfoStep {...props} />;
    case 2:
      return <OperatorsStep {...props} />;
    case 3:
      return <DisposalStep {...props} />;
    case 4:
      return <YardsStep {...props} />;
    case 5:
      return <FleetStep {...props} />;
    case 6:
      return <DriversStep {...props} />;
    case 7:
      return <JobTypesStep />;
    default:
      return <></>;
  }
}

export function GettingStartedSection(props: { readonly stepProps: StepProps }): React.JSX.Element {
  const { stepProps } = props;
  const { data: status } = useWizardStatus();
  const update = useUpdateWizardStatus();
  const [open, setOpen] = React.useState<number | null>(null);

  if (open !== null && open <= 7) {
    const stepDef = WIZARD_STEPS[open - 1];
    const complete = status?.sectionsComplete[open - 1] === true;
    return (
      <div>
        <button
          type="button"
          onClick={(): void => setOpen(null)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            minHeight: '44px',
            fontSize: '13px',
            color: 'var(--color-text-secondary)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            marginBottom: '12px',
          }}
        >
          <ArrowLeft size={14} aria-hidden="true" /> Back to Getting started
        </button>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h2 style={{ fontSize: '18px', fontWeight: 600 }}>
            {open}. {stepDef?.title}
          </h2>
          {stepProps.canManage && (
            <Button
              type="button"
              variant="outline"
              disabled={update.isPending}
              onClick={(): void =>
                update.mutate(
                  { section: open, complete: !complete },
                  {
                    onSuccess: () =>
                      toast.success(complete ? 'Marked incomplete' : 'Marked complete'),
                  },
                )
              }
            >
              {complete ? 'Mark incomplete' : 'Mark complete'}
            </Button>
          )}
        </div>
        {renderStep(open, stepProps)}
      </div>
    );
  }

  return (
    <div>
      <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
        {status !== undefined ? `${status.completionPct}% complete` : 'Loading…'} — edit any section
        independently. Changes save immediately.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: '12px',
        }}
      >
        {WIZARD_STEPS.filter((s) => s.n <= 7).map((s) => {
          const complete = status?.sectionsComplete[s.n - 1] === true;
          return (
            <div
              key={s.n}
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-card)',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: complete ? 'var(--color-success)' : 'var(--color-warning)',
                  }}
                >
                  {complete ? '✓ Complete' : '○ Incomplete'}
                </span>
              </div>
              <div style={{ fontWeight: 600 }}>{s.title}</div>
              <div>
                <Button type="button" variant="outline" onClick={(): void => setOpen(s.n)}>
                  Edit
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
